import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, ClipboardList, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Checklists() {
  const { selectedId } = useProperty();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [list, setList] = useState([]);

  const load = useCallback(async () => {
    if (!selectedId) return;
    const [c, z] = await Promise.all([
      api.get(`/checklists?property_id=${selectedId}`),
      api.get(`/zones?property_id=${selectedId}`),
    ]);
    setList(c.data);
    setZones(z.data);
  }, [selectedId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/checklists/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const duplicate = async (c) => {
    const name = window.prompt(`Duplicate "${c.name}" as:`, `${c.name} (copy)`);
    if (!name) return;
    try {
      await api.post(`/checklists/${c.id}/duplicate`, { name });
      toast.success(`Duplicated: ${name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to duplicate");
    }
  };

  if (!selectedId) return <div className="text-zinc-500">Select a property.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="label-overline">Templates</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Checklists</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-lg">
            Recurring task templates per zone — what your team should do, when, and how.
          </p>
        </div>
        <Button
          onClick={() => navigate("/checklists/new")}
          className="bg-zinc-950 hover:bg-zinc-800 h-11"
          data-testid="new-checklist-button"
        >
          <Plus className="w-4 h-4 mr-2" /> New Checklist
        </Button>
      </div>

      {list.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-zinc-200 rounded-md py-16 text-center" data-testid="checklists-empty">
          <ClipboardList className="w-8 h-8 mx-auto text-zinc-300 mb-2" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-bold tracking-tight">No checklists yet</h3>
          <p className="text-sm text-zinc-500 mt-1 max-w-xs mx-auto">
            Build your first one — it takes about a minute.
          </p>
          <Button
            onClick={() => navigate("/checklists/new")}
            className="bg-zinc-950 hover:bg-zinc-800 mt-4"
            data-testid="empty-create-checklist"
          >
            <Plus className="w-4 h-4 mr-2" /> Create a checklist
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((c) => {
            const zone = zones.find((z) => z.id === c.zone_id);
            return (
              <div key={c.id} className="bg-white border border-zinc-200 rounded-md p-5 hover:shadow-sm transition" data-testid={`checklist-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/checklists/${c.id}/edit`} className="min-w-0 flex-1" data-testid={`open-checklist-${c.id}`}>
                    <div className="label-overline">{zone?.name || "—"}</div>
                    <h3 className="font-display text-xl font-bold tracking-tight mt-1 truncate">{c.name}</h3>
                    <div className="text-xs text-zinc-500 mt-0.5">{c.items?.length || 0} task{c.items?.length === 1 ? "" : "s"}</div>
                  </Link>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => navigate(`/checklists/${c.id}/edit`)} className="p-2 hover:bg-zinc-100 rounded" data-testid={`edit-checklist-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(c)} className="p-2 hover:bg-red-50 hover:text-red-600 rounded" data-testid={`delete-checklist-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <ul className="mt-4 space-y-1.5">
                  {(c.items || []).slice(0, 4).map((it) => (
                    <li key={it.id} className="text-sm flex items-center justify-between py-1 border-b border-zinc-100 last:border-0 gap-2">
                      <span className="truncate">{it.name}</span>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">{(it.frequency || "").replace("_", " ")}</span>
                    </li>
                  ))}
                  {c.items?.length > 4 && (
                    <li className="text-xs text-zinc-400 pt-1">+{c.items.length - 4} more</li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
